"use client"

import { useTranslations } from "next-intl"
import { useMutation } from "@tanstack/react-query"

import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  excludeConversationContactAction,
  includeConversationContactAction,
  type ContactExclusionsView,
} from "@/server-actions/message-agent-exclusions"

/**
 * Shared with the unified inbox's action hook, which performs the same two writes
 * from its own header. One wording, so the two inboxes cannot report the same
 * outcome differently.
 */
export function useContactExclusionToasts() {
  const tUi = useTranslations("Feedback.ContactExclusions")
  return {
  exclude: {
    loading: tUi("excludingContact"),
    success: tUi("contactExcludedAIWillNotReplyToThem"),
  },
  include: {
    loading: tUi("removingFromExclusionList"),
    success: tUi("contactRemovedAIWillReplyToThemAgain"),
  },
} as const
}

export function useExcludeConversationContactMutation() {
  const CONTACT_EXCLUSION_TOASTS = useContactExclusionToasts()

  const withQueryHandling = useTranslatedQueryHandling()

  return useMutation({
    mutationFn: async (input: { conversationId: string }): Promise<ContactExclusionsView> => {
      const result = await withQueryHandling(
        excludeConversationContactAction(input),
        CONTACT_EXCLUSION_TOASTS.exclude,
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useIncludeConversationContactMutation() {
  const CONTACT_EXCLUSION_TOASTS = useContactExclusionToasts()

  const withQueryHandling = useTranslatedQueryHandling()

  return useMutation({
    mutationFn: async (input: { conversationId: string }): Promise<ContactExclusionsView> => {
      const result = await withQueryHandling(
        includeConversationContactAction(input),
        CONTACT_EXCLUSION_TOASTS.include,
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}
